// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Dashboard: headline figures, volume by outcome, top event types, recent
// failures, and worker health. Follows the global application scope.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CheckCircle2, CircleAlert } from 'lucide-react'
import { statsOverviewQuery } from '@/api/queries/stats'
import type { StatsOverview, StatsRange } from '@/api/types'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState } from '@/components/QueryState'
import { RelativeTime } from '@/components/RelativeTime'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { IncludeTestSwitch } from '@/features/ops/IncludeTestSwitch'
import { MessageRows } from '@/features/ops/MessageRows'
import { StatLedger } from '@/features/ops/StatFigures'
import { VolumeChart } from '@/features/ops/VolumeChart'
import { formatNumber } from '@/lib/format'
import { useAppScope } from '@/shell/useAppScope'

const RANGES = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
] as const satisfies readonly { value: StatsRange; label: string }[]

export const Route = createFileRoute('/')({
  staticData: { crumb: 'Dashboard' },
  validateSearch: (s: Record<string, unknown>): { range?: StatsRange; include_test?: true } => ({
    range: s.range === '7d' || s.range === '30d' || s.range === '24h' ? s.range : undefined,
    // Test traffic is left out unless asked for; only `true` is kept in the URL.
    include_test: s.include_test === true || s.include_test === 'true' ? true : undefined,
  }),
  component: Dashboard,
})

function Dashboard() {
  const { range = '24h', include_test: includeTest = false } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { appId, application } = useAppScope()
  const stats = useQuery(statsOverviewQuery({ app: appId, range, includeTest }))

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          application
            ? `Delivery health for ${application.name}.`
            : 'Delivery health across every application.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-4">
            <IncludeTestSwitch
              checked={includeTest}
              onCheckedChange={(on) =>
                void navigate({
                  search: (prev) => ({ ...prev, include_test: on ? true : undefined }),
                  replace: true,
                })
              }
            />
            <Segmented
              aria-label="Time range"
              size="md"
              value={range}
              onValueChange={(r) =>
                void navigate({
                  search: (prev) => ({ ...prev, range: r === '24h' ? undefined : r }),
                })
              }
              options={RANGES}
            />
          </div>
        }
      />
      {stats.isError ? (
        <Card>
          <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
        </Card>
      ) : (
        <div className="space-y-5">
          <StatLedger stats={stats.data} />
          <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Card>
              <CardHeader
                title="Volume by outcome"
                description={
                  includeTest
                    ? 'Messages accepted in each period, by current status. Includes test messages.'
                    : 'Messages accepted in each period, by current status. Test messages aren’t counted.'
                }
              />
              <CardBody>
                {stats.data ? (
                  <VolumeChart
                    series={stats.data.series}
                    range={range}
                    showCaptured={includeTest}
                  />
                ) : (
                  <Skeleton className="h-64" />
                )}
              </CardBody>
            </Card>
            <TopEvents stats={stats.data} appId={appId} range={range} />
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <RecentFailures stats={stats.data} appId={appId} />
            <Workers stats={stats.data} />
          </div>
        </div>
      )}
    </>
  )
}

const PROBLEM_STATUSES = ['failed', 'bounced', 'complained'] as const

function TopEvents({
  stats,
  appId,
  range,
}: {
  stats: StatsOverview | undefined
  appId?: string
  range: StatsRange
}) {
  const items = stats?.top_events ?? []
  const max = Math.max(1, ...items.map((e) => e.count))
  return (
    <Card>
      <CardHeader title="Top event types" description="By messages accepted in this period." />
      <CardBody>
        {!stats ? (
          <SkeletonText lines={5} />
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-2">No events in this period</p>
        ) : (
          <ol className="space-y-2.5">
            {items.slice(0, 8).map((e) => (
              <li key={e.event_key}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <Link
                    to="/messages"
                    search={{ app: appId, event: e.event_key, range }}
                    className="truncate font-mono text-xs text-ink hover:text-brass hover:underline"
                  >
                    {e.event_key}
                  </Link>
                  <span className="tabular shrink-0 text-xs text-ink-2">
                    {formatNumber(e.count)}
                    {e.problems > 0 && (
                      <>
                        {' · '}
                        <Link
                          to="/messages"
                          search={{
                            app: appId,
                            event: e.event_key,
                            range,
                            status: [...PROBLEM_STATUSES],
                          }}
                          className="text-danger hover:underline"
                        >
                          {formatNumber(e.problems)} problems
                        </Link>
                      </>
                    )}
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-surface-3">
                  <div
                    className="h-1 rounded-full bg-brass-line"
                    style={{ width: `${(e.count / max) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  )
}

function RecentFailures({ stats, appId }: { stats: StatsOverview | undefined; appId?: string }) {
  const items = stats?.recent_failures ?? []
  return (
    <Card>
      <CardHeader
        title="Recent failures"
        description="Bounces, complaints, and failed deliveries."
        actions={
          <Link
            to="/messages"
            search={{ app: appId, status: [...PROBLEM_STATUSES] }}
            className="text-xs font-medium text-brass hover:underline"
          >
            Open message log
          </Link>
        }
      />
      {!stats ? (
        <CardBody>
          <SkeletonText lines={4} />
        </CardBody>
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon={CheckCircle2}
          title="No failures"
          description="Every message in this period was delivered or is still in flight."
        />
      ) : (
        <MessageRows messages={items.slice(0, 8)} showError />
      )}
    </Card>
  )
}

/** Worker heartbeats come with the stats overview, so this refreshes with it. */
function Workers({ stats }: { stats: StatsOverview | undefined }) {
  const w = stats?.workers
  return (
    <Card>
      <CardHeader
        title="Workers"
        description={
          w
            ? `${w.alive} of ${w.total} reporting a heartbeat.`
            : 'Delivery workers report a heartbeat every few seconds.'
        }
        actions={
          <Link to="/system" className="text-xs font-medium text-brass hover:underline">
            System details
          </Link>
        }
      />
      <CardBody>
        {!w ? (
          <SkeletonText lines={3} />
        ) : w.alive === 0 ? (
          <p className="flex items-center gap-2 py-4 text-sm text-danger">
            <CircleAlert className="size-4 shrink-0" /> No workers are running. Queued messages
            won’t be delivered.
          </p>
        ) : (
          <ul className="space-y-2">
            {w.workers
              .filter((x) => x.alive)
              .map((worker) => (
                <li key={worker.worker_id} className="flex items-center gap-2.5 text-sm">
                  <span className="size-2 shrink-0 rounded-full bg-success" aria-hidden />
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink">
                    {worker.worker_id}
                  </span>
                  <span className="shrink-0 text-xs text-ink-2">
                    {worker.role}, seen <RelativeTime value={worker.seen_at} />
                  </span>
                </li>
              ))}
            {w.total > w.alive && (
              <li className="flex items-center gap-2.5 text-xs text-ink-3">
                <span className="size-2 shrink-0 rounded-full bg-line-strong" aria-hidden />
                {formatNumber(w.total - w.alive)} stale{' '}
                {w.total - w.alive === 1 ? 'heartbeat' : 'heartbeats'} from stopped or restarted
                workers
              </li>
            )}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
